import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser } from '@/features/auth/types/auth.types';
import { authApi } from '@/features/auth/api';
import { authUserFromAbpApplicationConfiguration } from '@/features/auth/mapApplicationConfigurationUser';
import { userHasAnyRoleCaseInsensitive, userHasRoleCaseInsensitive } from '@/features/auth/roleAccess';
import { ApiError } from '@/shared/api';
import { apiConfig } from '@/shared/config/api.config';

interface LoginCredentials {
  userNameOrEmailAddress: string;
  password: string;
  rememberMe: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  const loadProfile = useCallback(async () => {
    try {
      const config = await authApi.getApplicationConfiguration();
      setUser(authUserFromAbpApplicationConfiguration(config));
    } catch (err) {
      console.error('[AuthProvider] loadProfile failed', err);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    loadProfile().finally(() => setIsLoading(false));
  }, [loadProfile]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      const res = await authApi.login(credentials);
      if (res.result !== 1) {
        throw new ApiError(res.description, 200, res);
      }
      const config = await authApi.getApplicationConfiguration();
      const nextUser = authUserFromAbpApplicationConfiguration(config);
      if (!nextUser) {
        throw new ApiError('Signed in but could not load session (application configuration).', 200, res);
      }
      setUser(nextUser);
    } catch (err) {
      console.error('[AuthProvider] login failed', err);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('[AuthProvider] logout API failed', err);
    } finally {
      setUser(null);
      try {
        localStorage.removeItem(apiConfig.authAccessTokenKey);
      } catch {
        // ignore
      }
    }
  }, []);

  const hasRole = useCallback(
    (role: string) => userHasRoleCaseInsensitive(user?.roles, role),
    [user?.roles]
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => userHasAnyRoleCaseInsensitive(user?.roles, roles),
    [user?.roles]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      setUser,
      hasRole,
      hasAnyRole,
    }),
    [user, isAuthenticated, isLoading, login, logout, hasRole, hasAnyRole]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
